import { useContext } from 'react';

import {
  alpha,
  Box,
  darken,
  Divider,
  IconButton,
  lighten,
  Stack,
  styled,
  Tooltip,
  useTheme
} from '@mui/material';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { SidebarContext } from 'src/contexts/SidebarContext';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';
import HeaderUserbox from './Userbox';
import HeaderMenu from './Menu';
import { useTour } from 'src/contexts/TourContext';

const HeaderWrapper = styled(Box)(
  ({ theme }) => `
        height: ${theme.header.height};
        color: ${theme.header.textColor};
        padding: ${theme.spacing(0, 2)};
        right: 0;
        z-index: 6;
        background-color: ${alpha(theme.header.background, 0.95)};
        backdrop-filter: blur(3px);
        position: fixed;
        justify-content: space-between;
        width: 100%;
        @media (min-width: ${theme.breakpoints.values.lg}px) {
            left: ${theme.sidebar.width};
            width: auto;
        }
`
);

interface HeaderProps {
  language: string;
  onSelectLanguage: (item: string) => void;
}

function Header(props: HeaderProps) {
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const { startTour } = useTour();
  const theme = useTheme();
  const isMobile = window.innerWidth <= 1280;

  return (
    <HeaderWrapper
      display="flex"
      alignItems="center"
      sx={{
        backgroundColor: isMobile
          ? darken(theme.colors.alpha.black[100], 0.5)
          : '',

        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 1px 0 ${alpha(
                lighten(theme.colors.primary.main, 0.7),
                0.15
              )}, 0px 2px 8px -3px rgba(0, 0, 0, 0.2), 0px 5px 22px -4px rgba(0, 0, 0, .1)`
            : `0px 2px 8px -3px ${alpha(
                theme.colors.alpha.black[100],
                0.2
              )}, 0px 5px 22px -4px ${alpha(
                theme.colors.alpha.black[100],
                0.1
              )}`
      }}
    >
      <Box
        component="span"
        sx={{
          ml: 2,
          display: { lg: 'none', xs: 'inline-block' }
        }}
      >
        <Tooltip arrow title="Toggle Menu">
          <IconButton sx={{ color: 'white' }} onClick={toggleSidebar}>
            {!sidebarToggle ? (
              <MenuTwoToneIcon fontSize="small" />
            ) : (
              <CloseTwoToneIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        alignItems="right"
        spacing={6}
      ></Stack>

      <Box display="flex" alignItems="center">
        <Tooltip arrow title="Take a Tour">
          <IconButton
            sx={{ color: isMobile ? 'white' : theme.header.textColor }}
            onClick={startTour}
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
        <HeaderMenu
          language={props.language}
          onSelectLanguage={props.onSelectLanguage}
        />

        <HeaderUserbox />
      </Box>
    </HeaderWrapper>
  );
}

export default Header;
